import type { FC } from 'react';
import { Box, Stack, Tooltip } from '@mui/material';

import type {
  UpstreamApiDashboardData,
  WidgetConfig,
} from 'src/types/dashboard';
import { numberOrDash } from 'src/helper/helper';
import DoughnutChart from 'src/components/dashboard/charts/DoughnutChart';
import useChatbotData from 'src/components/dashboard/DashboardPage/widgets/DoughnutWidget/useChatbotData';
import useQuestionData from 'src/components/dashboard/hooks/useQuestionData';

import { styles } from './WidgetViewCommonWidget.styles';

export const DOUGHNUT_WIDGET_TEST_ID = 'doughnut-widget';

export interface WidgetViewDoughnutWidgetProps {
  data: UpstreamApiDashboardData;
  comparisonData?: UpstreamApiDashboardData;
  widgetConfig: WidgetConfig;
}

export const WidgetViewDoughnutWidget: FC<WidgetViewDoughnutWidgetProps> = ({
  data,
  widgetConfig,
}) => {
  const { chartData, comparisonDataName, comparisonAggregate, focalAggregate } =
    useQuestionData(
      widgetConfig.comparison_type,
      widgetConfig.questions[0] ?? '',
      data,
    );

  useChatbotData(comparisonAggregate, focalAggregate);

  if (!focalAggregate) return null;

  return (
    <Stack
      direction="column"
      spacing={1}
      data-test-id={DOUGHNUT_WIDGET_TEST_ID}
      sx={{ ...styles.wrapper, justifyContent: 'space-between' }}
    >
      <Stack direction="row" sx={{ justifyContent: 'center', flexGrow: 1 }}>
        <Stack direction="column" sx={{ justifyContent: 'center' }}>
          <DoughnutChart
            data={chartData}
            percentageValue={numberOrDash(focalAggregate.ptop2)}
            diffValue={focalAggregate.ptop2_yoyDiff}
            bigVersion
            sx={styles.chart}
          />
        </Stack>
      </Stack>
      <Box sx={{ pb: 2 }}>
        <Box sx={styles.headline}>{focalAggregate.label}</Box>

        {comparisonDataName && (
          <Tooltip arrow placement="bottom" title={comparisonDataName}>
            <Stack direction="row" spacing={0.5} sx={styles.label}>
              <Box sx={styles.labelText}>{comparisonDataName}</Box>
              <Box sx={styles.percentage}>
                {numberOrDash(comparisonAggregate?.ptop2, true)}
              </Box>
            </Stack>
          </Tooltip>
        )}
      </Box>
    </Stack>
  );
};

export default WidgetViewDoughnutWidget;
